import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useCookies } from 'react-cookie';
import PropTypes from 'prop-types';

import Select from 'components/bronson/Select';
import Input from 'components/bronson/Input';

import TransportService from 'services/TransportService';
import RentingService from 'services/RentingService';
import Globals from '../../../../../Globals';

const Directions = ({
    t,
    values,
    setFieldValue,
    customKeys,
    renderError,
    disabled,
}) => {
    const isMobile = useMediaQuery({ query: '(max-width: 600px)' });
    const [cookies] = useCookies([Globals.COOKIES.JWT]);
    const accessToken = cookies?.[Globals.COOKIES.JWT]?.AccessToken;
    const defaultSelectItem = useMemo(
        () => ({ value: '', text: t.getTraduction('Select') }),
        [t]
    );
    const [streetTypes, setStreetTypes] = useState([defaultSelectItem]);
    const [poblations, setPoblations] = useState([defaultSelectItem]);

    useEffect(() => {
        if (
            poblations.length === 1 &&
            values[customKeys?.poblation || 'poblation']
        ) {
            setPoblations([
                {
                    value: values[customKeys?.poblation || 'poblation'],
                    text: values[customKeys?.poblation || 'poblation'],
                },
                {},
            ]);
        }
    }, [customKeys?.poblation, poblations.length, values]);

    const streetTypeValue = values[customKeys?.streetType || 'streetType'];
    useEffect(() => {
        if (streetTypes.length === 1 && streetTypeValue) {
            setStreetTypes([
                {
                    value: streetTypeValue,
                    text: streetTypeValue,
                },
            ]);
        }
    }, [customKeys?.streetType, streetTypes.length, streetTypeValue]);

    useEffect(() => {
        if (
            values?.[customKeys?.postalCode || 'postalCode']?.length === 5 &&
            poblations.length === 1
        ) {
            const cp = customKeys
                ? values[customKeys.postalCode]
                : values.postalCode;

            if (!values[customKeys?.city || 'city']) {
                (async function fetchProvince() {
                    const response = await new TransportService().getCodeProvince(
                        cookies?.[Globals.COOKIES.JWT]?.AccessToken,
                        cp
                    );

                    if (response?.Result)
                        setFieldValue(
                            customKeys?.city || 'city',
                            response?.Result
                        );
                })();

                if (!values[customKeys?.poblation || 'poblation']) {
                    (async function fetchPoblations() {
                        const response = await RentingService.getPoblation(
                            cookies?.[Globals.COOKIES.JWT]?.AccessToken,
                            cp
                        );

                        if (
                            response?.statusCode === 200 &&
                            response?.body?.Result
                        ) {
                            const parsedResponse = response?.body?.Result?.reduce(
                                (acc, curr) => {
                                    acc.push({
                                        value: curr.Id,
                                        text: curr.Description,
                                    });
                                    return acc;
                                },
                                [defaultSelectItem]
                            );
                            setPoblations(parsedResponse);
                        }
                    })();
                }
            }
        } else if (
            values[customKeys?.city || 'city'] &&
            values?.[customKeys?.postalCode || 'postalCode']?.length !== 5
        ) {
            setFieldValue(customKeys?.poblation || 'poblation', '');
            setFieldValue(customKeys?.city || 'city', '');
            setPoblations([defaultSelectItem]);
        }
    }, [
        cookies,
        customKeys,
        defaultSelectItem,
        poblations.length,
        setFieldValue,
        values,
    ]);

    const fetchStreetOptions = useCallback(async () => {
        if (values.autocompleted) return;
        try {
            const response = await new TransportService().getStreetType(
                accessToken
            );

            if (response?.statusCode === 200 && response?.body?.Result) {
                const parsedResponse = JSON.parse(
                    response?.body?.Result
                )?.reduce(
                    (acc, curr) => {
                        acc.push({
                            value: curr.CodeWay,
                            text: curr.DescriptionWay,
                        });
                        return acc;
                    },
                    [defaultSelectItem]
                );

                setStreetTypes(parsedResponse);
            }
        } catch (error) {
            console.log(error);
        }
    }, [accessToken, defaultSelectItem, values.autocompleted]);

    useEffect(() => {
        fetchStreetOptions();
    }, [fetchStreetOptions]);

    return (
        <>
            <div
                className={`${
                    isMobile
                        ? 'u-1/2 u-mb'
                        : 'u-inline-flex-item u-flex-start u-1/4'
                }`}
            >
                <Select
                    options={{
                        showLabel: true,
                        text: t.getTraduction('OptionsTypeVia'),
                        id: 'street-types-select',
                        values: streetTypes,
                        classContainer: 'u-1/1',
                        disabled,
                    }}
                    value={values[customKeys?.streetType || 'streetType']}
                    onChange={value =>
                        setFieldValue(
                            customKeys?.streetType || 'streetType',
                            value
                        )
                    }
                />
                {renderError(customKeys?.streetType || 'streetType')}
            </div>
            <div
                className={`${
                    isMobile
                        ? 'u-1/1 u-mb'
                        : 'u-inline-flex-item u-flex-start u-3/4 u-pl-large'
                }`}
            >
                <Input
                    options={{
                        label: t.getTraduction('StreetName'),
                        id: 'street-name-input',
                        type: 'text',
                        disabled,
                    }}
                    value={values[customKeys?.street || 'street']}
                    onChange={value =>
                        setFieldValue(customKeys?.street || 'street', value)
                    }
                />
                {renderError(customKeys?.street || 'street')}
            </div>
            <div className="u-1/1 u-mt">
                <div
                    className={`u-inline-flex-item u-flex-start u-3/12 ${
                        isMobile ? 'u-mr' : 'u-mr-large'
                    }`}
                >
                    <Input
                        options={{
                            label: t.getTraduction('Number'),
                            id: 'street-number-input',
                            type: 'number',
                            disabled,
                        }}
                        value={
                            values[customKeys?.streetNumber || 'streetNumber']
                        }
                        onChange={value =>
                            setFieldValue(
                                customKeys?.streetNumber || 'streetNumber',
                                value
                            )
                        }
                    />
                    {renderError(customKeys?.streetNumber || 'streetNumber')}
                </div>
                <div className="u-inline-flex-item u-flex-start u-3/12">
                    <Input
                        options={{
                            label: t.getTraduction('Apartment'),
                            id: 'street-floor-input',
                            type: 'text',
                            disabled,
                        }}
                        value={values[customKeys?.streetFloor || 'streetFloor']}
                        onChange={value =>
                            setFieldValue(
                                customKeys?.streetFloor || 'streetFloor',
                                value
                            )
                        }
                    />
                    {renderError(customKeys?.streetFloor || 'streetFloor')}
                </div>
                <div className="u-inline-flex-item u-flex-start u-5/12 u-float-right">
                    <Input
                        options={{
                            label: t.getTraduction('PostalCodeLabel'),
                            id: 'postal-code-input',
                            name: customKeys?.postalCode || 'postalCode',
                            type: 'number',
                            event: true,
                            disabled,
                        }}
                        value={values[customKeys?.postalCode || 'postalCode']}
                        onKeyPress={e =>
                            e.key.match(/[^0-9]/) && e.preventDefault()
                        }
                        onChange={({ target: { name, value } }) =>
                            setFieldValue(name, value)
                        }
                    />
                    {renderError(customKeys?.postalCode || 'postalCode')}
                </div>
            </div>
            <div className="u-1/1 u-mt">
                <div
                    className={`${
                        isMobile
                            ? 'u-1/1 u-mb'
                            : 'u-inline-flex-item u-flex-start u-1/2 u-pr'
                    }`}
                >
                    <Input
                        options={{
                            label: t.getTraduction('CityLabel'),
                            type: 'text',
                            id: 'city-input',
                            disabled: true,
                        }}
                        value={values[customKeys?.city || 'city']}
                        onChange={() => null}
                    />
                    {renderError(customKeys?.city || 'city')}
                </div>
                <div
                    className={`${
                        isMobile
                            ? 'u-1/1 u-mb'
                            : 'u-inline-flex-item u-flex-start u-1/2 u-pl'
                    }`}
                >
                    <Select
                        options={{
                            showLabel: true,
                            text: t.getTraduction('PoblationLabel'),
                            id: 'poblation-select',
                            values: poblations,
                            classContainer: 'u-1/1',
                            disabled,
                        }}
                        value={values[customKeys?.poblation || 'poblation']}
                        onChange={value =>
                            setFieldValue(
                                customKeys?.poblation || 'poblation',
                                value
                            )
                        }
                    />
                    {renderError(customKeys?.poblation || 'poblation')}
                </div>
            </div>
        </>
    );
};

Directions.propTypes = {
    t: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    values: function ({ customKeys, values }, propName, componentName) {
        if (customKeys) {
            for (let key in customKeys) {
                if (typeof values[key] !== 'string') {
                    return new Error(
                        `Invalid prop in key '${key}' of '${propName}' supplied to '${componentName}'. Expected 'string' and received '${typeof values[
                            key
                        ]}'`
                    );
                }
            }
        } else {
            const properties = [
                'streetType',
                'street',
                'streetNumber',
                'streetFloor',
                'postalCode',
                'city',
                'poblation',
            ];
            for (let key of properties) {
                if (typeof values[key] !== 'string') {
                    return new Error(
                        `Invalid prop in key '${key}' of '${propName}' supplied to '${componentName}'. Expected 'string' and received '${typeof values[
                            key
                        ]}'`
                    );
                }
            }
        }
    },
    customKeys: PropTypes.shape({
        streetType: PropTypes.string.isRequired,
        street: PropTypes.string.isRequired,
        streetNumber: PropTypes.string.isRequired,
        streetFloor: PropTypes.string.isRequired,
        postalCode: PropTypes.string.isRequired,
        city: PropTypes.string.isRequired,
        poblation: PropTypes.string.isRequired,
    }),
    renderError: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

export default Directions;
